/**
 * AddEditQuotesV2 — V2 port of the Quote edit form.
 *
 * Loads via `fetchOneQuote(id)` (GET /quotes/{id}) so we don't
 * pull the entire collection just to find one row. Same submit /
 * delete actions as v1 — addEditQuote + deleteQuote.
 *
 * Redux graph:
 *   • fetchOneQuote → state.quotes.quote
 *   • addEditQuote(JSON.stringify(payload), navigate)
 *   • deleteQuote(id, navigate)
 *
 * Sections:
 *   01 Quote          (text + source — both required)
 *   02 Danger zone    (edit-only)
 *
 * Quotes don't need a right-rail preview — they're plain text
 * with no library-tile representation.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import {
  fetchOneQuote,
  addEditQuote,
  deleteQuote,
} from '../../../actions/quoteActions';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  TextInput,
  Textarea,
} from '../../redesign/edit';

const AddEditQuotesV2 = forwardRef(function AddEditQuotesV2(
  { isEdit, selectedId, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const quote = useSelector((s) => s.quotes?.quote);

  const [state, setState] = useState({
    quote_text: '',
    quote_speaker_name: '',
    quote_speaker_title: '',
    isSetToEdit: false,
  });

  const [isSaving, setIsSaving] = useState(false);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  /* ─── Fetch on mount — single-record GET /quotes/{id}. */
  useEffect(() => {
    if (!isEdit) return;
    dispatch(fetchOneQuote(Number(selectedId)))
      .catch((error) => {
        console.error('Error fetching quote:', error);
      });
  }, [dispatch, isEdit, selectedId]);

  /* Hydrate form state once the quote arrives in the slice. */
  useEffect(() => {
    if (!isEdit || !quote) return;
    if (Number(quote.quote_id) !== Number(selectedId)) return;
    setState((prev) => {
      if (prev.isSetToEdit) return prev;
      return { ...quote, isSetToEdit: true };
    });
    if (onLoadedRef.current) {
      onLoadedRef.current({
        quote_speaker_name: quote.quote_speaker_name,
        quote_updated_at: quote.quote_updated_at ?? quote.updated_at ?? null,
      });
    }
  }, [isEdit, quote, selectedId]);

  /* ─── Handlers ─────────────────────────────────────────────── */
  const setField = (name, value) => setState((prev) => ({ ...prev, [name]: value }));

  const submit = useCallback(() => {
    if (!state.quote_text) {
      Swal.fire('Error', 'Please enter quote text', 'error');
      return;
    }
    if (!state.quote_speaker_name) {
      Swal.fire('Error', 'Please enter source', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...state,
        ...(isEdit && { quote_id: selectedId }),
      };
      const result = dispatch(addEditQuote(JSON.stringify(payload), () => navigate('/quotes')));
      if (result && typeof result.finally === 'function') {
        result.finally(() => setIsSaving(false));
      } else {
        setTimeout(() => setIsSaving(false), 3000);
      }
    } catch (error) {
      console.error('Error saving quote:', error);
      Swal.fire('Error', 'Failed to save quote', 'error');
      setIsSaving(false);
    }
  }, [state, isEdit, selectedId, dispatch, navigate]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submit();
  };

  useImperativeHandle(ref, () => ({ submit }), [submit]);

  /* ─── Delete (1:1 with v1) ──────────────────────────────── */
  const handleDelete = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteQuote(selectedId, () => navigate('/quotes')));
      }
    });
  };

  if (isEdit && !state.isSetToEdit) {
    return <LoadingBackdrop open />;
  }

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <form
        id="quote-form"
        onSubmit={handleSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
        style={{ minWidth: 0, maxWidth: 760 }}
      >
        {/* 01 Quote */}
        <FormSection title="Quote" num="01">
          <FieldBlock label="Quote text" required htmlFor="f-q-text">
            <Textarea
              id="f-q-text"
              name="quote_text"
              value={state.quote_text}
              onChange={(e) => setField('quote_text', e.target.value)}
              rows={4}
              required
              placeholder="“…”"
            />
          </FieldBlock>
          <FieldBlock label="Source" required htmlFor="f-q-source" noMargin>
            <TextInput
              id="f-q-source"
              name="quote_speaker_name"
              value={state.quote_speaker_name}
              onChange={(e) => setField('quote_speaker_name', e.target.value)}
              required
              placeholder="Who said it? (author, book title, etc.)"
            />
          </FieldBlock>
        </FormSection>

        {/* 02 Danger zone */}
        {isEdit && (
          <FormSection title="Danger zone" num="02">
            <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Button variant="danger" onClick={handleDelete} type="button">
                Delete quote
              </Button>
              <span className="text-text-muted" style={{ fontSize: 12 }}>
                Permanent — cannot be undone.
              </span>
            </div>
          </FormSection>
        )}
      </form>
    </>
  );
});

export default AddEditQuotesV2;
